/**
 * Host-only lobby extras for Scotland Yard: Mr. X selection, detective-ticket
 * difficulty, and the rule-set edition. All write through to `room.settings`
 * via the platform service.
 */

import { Divider, Group, SegmentedControl, Select, Stack, Text } from '@mantine/core'
import { IconMask, IconTicket, IconBook } from '@tabler/icons-react'
import type { Room } from '@mdev/common'
import { updateRoomSettings } from '../../lobby/service'
import type { DetectiveTicketMode, ScotlandYardSettings, SyEdition } from '../module'
import { SY } from '../theme'

interface Props {
  room: Room<ScotlandYardSettings>
  isHost: boolean
}

const TICKET_OPTIONS: { value: DetectiveTicketMode; label: string }[] = [
  { value: 'standard', label: 'Standard (11 / 8 / 4)' },
  { value: 'double-taxi', label: 'Double taxis (22 / 8 / 4)' },
  { value: 'double-all', label: 'Double all (22 / 16 / 8)' },
  { value: 'unlimited', label: 'Unlimited (easy mode)' },
]

const EDITION_OPTIONS: { value: SyEdition; label: string }[] = [
  { value: 'classic', label: 'Classic (MB 1985)' },
  { value: 'ravensburger', label: 'Ravensburger (2013+)' },
]

export function HostLobbyExtras( { room, isHost }: Props ) {
  if ( !isHost ) return null

  const setting = room.settings.mrXSelection
  const mode: 'random' | 'specific' = setting === 'random' ? 'random' : 'specific'
  const specificUid = setting !== 'random' ? setting.uid : undefined
  const ticketMode: DetectiveTicketMode = room.settings.detectiveTickets ?? 'standard'
  const edition: SyEdition = room.settings.edition ?? 'classic'

  // Always spread the existing settings so writing one field doesn't clobber
  // the others.
  function patch( next: Partial<ScotlandYardSettings> ) {
    return updateRoomSettings<ScotlandYardSettings>( room.code, { ...room.settings, ...next } )
  }

  async function setMrxMode( next: 'random' | 'specific' ) {
    if ( next === 'random' ) {
      await patch( { mrXSelection: 'random' } )
    } else {
      // Default to the first real player (skip the host display).
      const firstUid = Object.keys( room.players ).find( ( uid ) => uid !== room.hostUid )
      if ( !firstUid ) return
      await patch( { mrXSelection: { uid: firstUid } } )
    }
  }

  async function setSpecific( uid: string | null ) {
    if ( !uid ) return
    await patch( { mrXSelection: { uid } } )
  }

  // Use the map KEY as the option value: always a defined uid string even if a
  // partial player entry lacks its own `uid` field. Exclude the host; sort by name.
  const playerOptions = Object.entries( room.players )
    .filter( ( [ uid ] ) => uid !== room.hostUid )
    .map( ( [ uid, p ] ) => ( {
      value: uid,
      label: `${ p.avatar ?? '🕵️' } ${ p.displayName ?? 'Player' }`,
    } ) )
    .sort( ( a, b ) => a.label.localeCompare( b.label ) )

  const detectiveCount = Object.keys( room.players ).filter( ( uid ) => uid !== room.hostUid ).length

  return (
    <Stack
      gap="md"
      p="md"
      style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }`, borderRadius: 12 } }
    >
      <Stack gap="xs">
        <Group gap="xs">
          <IconMask size={ 18 } color="var(--mantine-color-yellow-4)" />
          <Text c="bright" fw={ 600 } size="sm">Who plays Mr. X?</Text>
        </Group>
        <SegmentedControl
          size="sm"
          value={ mode }
          onChange={ ( v ) => setMrxMode( v as 'random' | 'specific' ) }
          data={ [
            { label: 'Random', value: 'random' },
            { label: 'Pick a player', value: 'specific' },
          ] }
        />
        { mode === 'specific' && (
          <Select
            size="sm"
            placeholder="Choose Mr. X"
            value={ specificUid ?? null }
            onChange={ setSpecific }
            data={ playerOptions }
            comboboxProps={ { withinPortal: true } }
          />
        ) }
      </Stack>

      <Divider color={ SY.border } />

      <Stack gap="xs">
        <Group gap="xs">
          <IconTicket size={ 18 } color="var(--mantine-color-yellow-4)" />
          <Text c="bright" fw={ 600 } size="sm">Detective tickets</Text>
        </Group>
        <Select
          size="sm"
          value={ ticketMode }
          onChange={ ( v ) => { if ( v ) void patch( { detectiveTickets: v as DetectiveTicketMode } ) } }
          data={ TICKET_OPTIONS }
          comboboxProps={ { withinPortal: true } }
          allowDeselect={ false }
        />
        <Text size="xs" c="dimmed">
          { ticketMode === 'standard'
            ? 'Standard scarcity — the real challenge for detectives.'
            : ticketMode === 'unlimited'
              ? 'Detectives never run out of any ticket — easiest mode.'
              : 'More tickets for the detectives — a gentler game.' }
        </Text>
      </Stack>

      <Divider color={ SY.border } />

      <Stack gap="xs">
        <Group gap="xs">
          <IconBook size={ 18 } color="var(--mantine-color-yellow-4)" />
          <Text c="bright" fw={ 600 } size="sm">Edition (Mr. X tickets)</Text>
        </Group>
        <SegmentedControl
          size="sm"
          value={ edition }
          onChange={ ( v ) => void patch( { edition: v as SyEdition } ) }
          data={ EDITION_OPTIONS }
        />
        <Text size="xs" c="dimmed">
          { edition === 'ravensburger'
            ? `Mr. X gets ${ detectiveCount || '—' } black ticket${ detectiveCount === 1 ? '' : 's' } (one per detective) and 3 double-moves.`
            : 'Mr. X gets 5 black tickets and 2 double-moves (fixed).' }
        </Text>
      </Stack>
    </Stack>
  )
}
